/**
 * Unified Derived Job State — Production Launch Layer
 *
 * Single deterministic function that computes a human-readable,
 * cross-system job state used in QA, portal, dispatch, and internal UI.
 *
 * ADDITIVE — does not modify any existing data or engines.
 */

import type { Job, Issue } from "./types";
import { deriveFinancialClearance, type ClearanceStage } from "./financialClearance";
import type { InstallerWrapRecord } from "./installerWrapEngine";

// ── Unified State Types ──

export type UnifiedInstallState =
  | "not_started"
  | "in_progress"
  | "partial_continuation"
  | "blocked"
  | "install_complete"
  | "qa_review"
  | "completed";

export type UnifiedPaymentState =
  | "no_payment_required"
  | "deposit_pending"
  | "deposit_received"
  | "payment_collected"
  | "payment_pending"
  | "payment_deferred"
  | "financially_cleared"
  | "financial_hold";

export type UnifiedFollowUpState =
  | "none"
  | "return_visit_scheduled"
  | "follow_up_required"
  | "awaiting_resolution";

export interface DerivedJobState {
  install: UnifiedInstallState;
  payment: UnifiedPaymentState;
  followUp: UnifiedFollowUpState;
  /** Human-readable composite label for UI display */
  label: string;
  /** Short one-line description for dispatch/QA */
  summary: string;
  /** Whether job can proceed to next lifecycle step */
  canProgress: boolean;
  /** Blocking reasons if canProgress is false */
  blockers: string[];
}

// ── Derivation ──

export function deriveJobState(
  job: Job,
  issues: Issue[],
  wrapRecords: InstallerWrapRecord[],
): DerivedJobState {
  const jobIssues = issues.filter(i => i.jobId === job.id);
  const openIssues = jobIssues.filter(i => i.status === "Open" || i.status === "In Progress");
  const jobWraps = wrapRecords.filter(w => w.jobId === job.id).sort((a, b) => b.timestamp.localeCompare(a.timestamp));
  const latestWrap = jobWraps[0];

  const install = deriveInstallState(job, latestWrap);
  const payment = derivePaymentState(job, latestWrap);
  const followUp = deriveFollowUpState(latestWrap, openIssues);

  const blockers: string[] = [];

  // Completion blockers
  if (install === "install_complete" || install === "qa_review") {
    if (openIssues.length > 0) blockers.push(`${openIssues.length} unresolved issue(s)`);
    if (payment === "payment_pending" || payment === "deposit_pending") blockers.push("Payment not addressed");
    if (payment === "financial_hold") blockers.push("Financial hold — issue blocking payment");
  }

  // Scheduling blockers
  if (install === "not_started") {
    const clearance = deriveFinancialClearance(job, "pre_schedule");
    if (clearance.status === "blocked") blockers.push("Financial clearance required before scheduling");
  }

  const canProgress = blockers.length === 0;
  const label = buildLabel(install, payment, followUp);
  const summary = buildSummary(install, payment, followUp, openIssues.length);

  return { install, payment, followUp, label, summary, canProgress, blockers };
}

// ── Install State ──

function deriveInstallState(job: Job, latestWrap?: InstallerWrapRecord): UnifiedInstallState {
  if (job.currentStage === "Completed") return "completed";
  if (job.currentStage === "QA Review") return "qa_review";
  if (job.currentStage === "In Progress") {
    if (latestWrap) {
      if (latestWrap.outcome === "completed_today") return "install_complete";
      if (latestWrap.outcome === "blocked_issue") return "blocked";
      return "partial_continuation";
    }
    return "in_progress";
  }
  if (job.installStatus === "Install Complete") return "install_complete";
  return "not_started";
}

// ── Payment State ──

function derivePaymentState(job: Job, latestWrap?: InstallerWrapRecord): UnifiedPaymentState {
  const plan = job.financials?.paymentPlan;

  if (!plan) {
    // Legacy: check basic payment status
    const ps = job.financials?.paymentStatus;
    if (ps === "Paid in Full") return "financially_cleared";
    if (ps === "Partial Payment") return "deposit_received";
    if (ps === "Invoiced") return "payment_pending";
    return "no_payment_required";
  }

  const allPaid = plan.lineItems.every(li => li.status === "paid" || li.status === "waived");
  if (allPaid) return "financially_cleared";

  // Check wrap payment capture
  if (latestWrap?.paymentCapture) {
    if (latestWrap.paymentCapture.disposition === "collected") return "payment_collected";
    if (latestWrap.paymentCapture.disposition === "deferred") return "payment_deferred";
  }

  // Check settlement status from wrap
  if (latestWrap?.settlementStatus === "hold_issue") return "financial_hold";

  // Check deposit
  const depositItem = plan.lineItems.find(li => li.kind === "deposit");
  if (depositItem && depositItem.status !== "paid" && depositItem.status !== "waived") return "deposit_pending";
  if (depositItem && (depositItem.status === "paid" || depositItem.status === "waived")) {
    const postInstallPending = plan.lineItems.some(li => li.dueAt === "post_install" && li.status !== "paid" && li.status !== "waived");
    if (postInstallPending) return "payment_pending";
    return "deposit_received";
  }

  return "payment_pending";
}

// ── Follow-Up State ──

function deriveFollowUpState(latestWrap?: InstallerWrapRecord, openIssues: Issue[] = []): UnifiedFollowUpState {
  if (!latestWrap) return "none";
  if (latestWrap.schedulingFollowUpRequired) {
    if (latestWrap.customerComm.proposedNextDate) return "return_visit_scheduled";
    return "follow_up_required";
  }
  if (openIssues.length > 0 && latestWrap.outcome === "blocked_issue") return "awaiting_resolution";
  return "none";
}

// ── Label Builder ──

function buildLabel(install: UnifiedInstallState, payment: UnifiedPaymentState, followUp: UnifiedFollowUpState): string {
  const installLabels: Record<UnifiedInstallState, string> = {
    not_started: "Pre-Install",
    in_progress: "Install In Progress",
    partial_continuation: "Install In Progress — Continuation",
    blocked: "Install Blocked",
    install_complete: "Install Complete",
    qa_review: "QA Review",
    completed: "Completed",
  };

  const paymentSuffix: Partial<Record<UnifiedPaymentState, string>> = {
    payment_collected: "Payment Collected",
    payment_pending: "Payment Pending",
    payment_deferred: "Payment Deferred",
    deposit_pending: "Deposit Pending",
    financial_hold: "Financial Hold",
    financially_cleared: "Financially Cleared",
  };

  const base = installLabels[install];
  const suffix = paymentSuffix[payment];

  if (followUp === "return_visit_scheduled") return `${base} — Return Visit Scheduled`;
  if (followUp === "follow_up_required") return `${base} — Follow-Up Required`;
  if (suffix && install !== "not_started") return `${base} — ${suffix}`;
  return base;
}

// ── Summary Builder ──

function buildSummary(install: UnifiedInstallState, payment: UnifiedPaymentState, followUp: UnifiedFollowUpState, openIssueCount: number): string {
  const parts: string[] = [];

  switch (install) {
    case "completed": parts.push("Project complete"); break;
    case "qa_review": parts.push("Pending QA review"); break;
    case "install_complete": parts.push("Install finished"); break;
    case "in_progress": parts.push("Install underway"); break;
    case "partial_continuation": parts.push("Partial install — continuation needed"); break;
    case "blocked": parts.push("Install blocked by issue"); break;
    case "not_started": parts.push("Awaiting install start"); break;
  }

  if (openIssueCount > 0) parts.push(`${openIssueCount} open issue(s)`);

  switch (payment) {
    case "payment_collected": parts.push("payment collected"); break;
    case "payment_pending": parts.push("payment due"); break;
    case "payment_deferred": parts.push("payment deferred"); break;
    case "deposit_pending": parts.push("deposit pending"); break;
    case "financial_hold": parts.push("financial hold"); break;
    case "financially_cleared": parts.push("financially clear"); break;
  }

  if (followUp === "return_visit_scheduled") parts.push("return visit scheduled");
  if (followUp === "follow_up_required") parts.push("follow-up required");

  return parts.join(" · ");
}

// ── Convenience: Scheduling-specific clearance ──

export function deriveSchedulingClearance(job: Job): { allowed: boolean; reason: string } {
  const clearance = deriveFinancialClearance(job, "pre_schedule");
  if (clearance.status === "blocked") {
    return { allowed: false, reason: clearance.reason };
  }
  // Also check material readiness
  if (job.materialReadiness === "Missing Items" || job.materialReadiness === "Not Ordered") {
    return { allowed: false, reason: "Materials not ready for scheduling" };
  }
  return { allowed: true, reason: "All scheduling prerequisites met" };
}

// ── Convenience: Install-day clearance ──

export function deriveInstallClearance(job: Job): { allowed: boolean; reason: string } {
  const clearance = deriveFinancialClearance(job, "pre_install");
  if (clearance.status === "blocked") {
    return { allowed: false, reason: clearance.reason };
  }
  if (job.materialReadiness !== "Loaded for Crew" && job.materialReadiness !== "Staged for Job" && job.materialReadiness !== "Fully Received") {
    return { allowed: false, reason: "Materials not loaded or staged for crew" };
  }
  if (job.permitRequired && job.permitStatus !== "Approved" && job.permitStatus !== "Not Required") {
    return { allowed: false, reason: "Permit not yet approved" };
  }
  return { allowed: true, reason: "All install prerequisites met" };
}

// ── Financial Integrity Check ──
//
// CCC pass: discrepancies are no longer treated equally. Each is tagged
// HIGH (real cash/funding exposure that should feed Maverick) or LOW
// (rounding / financing artifacts that should NOT trigger predictions).
// Sub-cent noise is ignored entirely.
//
// `valid` is preserved for backward compat and is true only when there
// are no HIGH discrepancies (LOW drift no longer "blocks the world").

export type DiscrepancySeverity = "HIGH" | "LOW";

export interface ClassifiedDiscrepancy {
  severity: DiscrepancySeverity;
  message: string;
}

export interface FinancialIntegrityResult {
  valid: boolean;
  discrepancies: string[];
  classified: ClassifiedDiscrepancy[];
  hasHigh: boolean;
}

/**
 * Threshold (in dollars) below which financial drift is treated as benign
 * rounding/financing noise (LOW severity) rather than real exposure (HIGH).
 * Tunable in one place — keep behavior changes intentional.
 */
export const FINANCIAL_NOISE_THRESHOLD = 1.0;

export function checkFinancialIntegrity(job: Job): FinancialIntegrityResult {
  const classified: ClassifiedDiscrepancy[] = [];
  const plan = job.financials?.paymentPlan;
  const contractValue = job.financials?.contractValue ?? 0;

  const push = (severity: DiscrepancySeverity, message: string) =>
    classified.push({ severity, message });

  if (plan) {
    // Plan total vs contract — classify by drift magnitude
    const planTotal = plan.lineItems.reduce((s, li) => s + li.amount, 0);
    const planDrift = Math.abs(planTotal - contractValue);
    if (planDrift > 0.01) {
      const sev: DiscrepancySeverity = planDrift >= FINANCIAL_NOISE_THRESHOLD ? "HIGH" : "LOW";
      push(
        sev,
        `Payment plan total ($${planTotal.toLocaleString()}) differs from contract value ($${contractValue.toLocaleString()})`,
      );
    }

    // Duplicate invoices that materially affect totals = HIGH
    const invoices = job.financials?.invoices ?? [];
    const depositInvoices = invoices.filter(i => i.type === "deposit");
    if (depositInvoices.length > 1) {
      push("HIGH", `Duplicate deposit invoices found (${depositInvoices.length})`);
    }
    const finalInvoices = invoices.filter(i => i.type === "final");
    if (finalInvoices.length > 1) {
      push("HIGH", `Duplicate final invoices found (${finalInvoices.length})`);
    }

    // Paid + pending + waived vs contract — classify by drift magnitude
    const totalPaid = (job.financials?.payments ?? []).reduce((s, p) => s + p.amount, 0);
    const totalPending = plan.lineItems
      .filter(li => li.status !== "paid" && li.status !== "waived")
      .reduce((s, li) => s + li.amount, 0);
    const totalWaived = plan.lineItems
      .filter(li => li.status === "waived")
      .reduce((s, li) => s + li.amount, 0);

    const reconcileDrift = Math.abs((totalPaid + totalPending + totalWaived) - contractValue);
    if (reconcileDrift > 0.01) {
      const sev: DiscrepancySeverity = reconcileDrift >= FINANCIAL_NOISE_THRESHOLD ? "HIGH" : "LOW";
      push(
        sev,
        `Paid ($${totalPaid.toLocaleString()}) + Pending ($${totalPending.toLocaleString()}) + Waived ($${totalWaived.toLocaleString()}) ≠ Contract ($${contractValue.toLocaleString()})`,
      );
    }
  } else {
    // Legacy: invoice total exceeding contract value is real exposure
    const invoices = job.financials?.invoices ?? [];
    const invoicedTotal = invoices.reduce((s, i) => s + i.amount, 0);
    if (invoicedTotal > contractValue && contractValue > 0) {
      push(
        "HIGH",
        `Invoiced total ($${invoicedTotal.toLocaleString()}) exceeds contract value ($${contractValue.toLocaleString()})`,
      );
    }
  }

  const hasHigh = classified.some(c => c.severity === "HIGH");
  return {
    valid: !hasHigh,
    discrepancies: classified.map(c => c.message),
    classified,
    hasHigh,
  };
}

// ── CCC: Post-Completion Financial Exception ──
//
// Outcome-based, next-morning trigger. Surfaces real cash/funding exposure
// once a job has been operationally completed but money has not landed by
// the start of the next day. Does NOT downgrade the job — this is an
// exposure layer, not a stage rollback.
//
// Completion timing is derived from `activityHistory` (no schema change).
// If the activity log has no trustworthy completion entry, we fail
// conservatively and return null — the engine will stay silent rather
// than fabricate timing.
//
// Financing branch note: for this pass we INFER "funding not confirmed"
// from `financials.financing.financingStatus === "approved"` combined with
// derived payment state still not fully settled. There is no dedicated
// disbursement signal in the model today; if/when one is added, swap it in
// here.

export type FinancialExceptionKind =
  | "cash_not_collected"
  | "funding_not_confirmed";

export interface PostCompletionFinancialException {
  kind: FinancialExceptionKind;
  /** Outstanding amount still owed (best-effort from plan + payments). */
  outstandingAmount: number;
  /** ISO date when the job transitioned to Completed. */
  completedAt: string;
}

/** Start of "today" in the local runtime — completion before this fires the trigger. */
function startOfTodayMs(): number {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d.getTime();
}

/**
 * Single source of truth for "does this activity entry represent a transition
 * into the Completed stage?" Centralizing this keeps regex fragility in one
 * tested place instead of scattered across the codebase.
 *
 * FUTURE DURABILITY UPGRADE (option a): when we next touch automationRunner,
 * emit a structured stage-transition activity (e.g. action constant
 * "STAGE_TRANSITION:Completed" or a dedicated typed field) and switch this
 * helper to read the structured signal first, falling back to the regex for
 * legacy entries. Until then, this remains text-derived but localized.
 */
const COMPLETION_ACTIVITY_RE =
  /\b(stage|status|marked|moved|job)\b.*\bcompleted\b|\bcompleted\b.*\bstage\b/i;

export function isCompletionActivity(entry: { action?: unknown; date?: unknown }): boolean {
  if (typeof entry.action !== "string") return false;
  if (typeof entry.date !== "string" || !entry.date) return false;
  return COMPLETION_ACTIVITY_RE.test(entry.action);
}

/**
 * Derive completion timing from activity history. Looks for the most recent
 * activity entry indicating a transition into the Completed stage. Returns
 * null when no such entry exists — caller MUST treat null as "unknown" and
 * not fire the exception.
 */
export function deriveCompletionDate(job: Job): string | null {
  const entries = job.activityHistory ?? [];
  const matches = entries
    .filter(isCompletionActivity)
    .sort((a, b) => b.date.localeCompare(a.date));
  return matches[0]?.date ?? null;
}

/**
 * Single source of truth for "is this a financed deal?" — currently inferred
 * from financing.financingStatus === "approved" OR paymentPlan.type === "financed".
 *
 * INFERENCE NOTE: there is no dedicated funding/disbursement field on the
 * model today. When one is added (e.g. financing.fundingDisbursed), update
 * this helper and any "funding not confirmed" branch logic — this is the
 * one place to change.
 */
export function isFinancedDeal(job: Job): boolean {
  const financingStatus = job.financials?.financing?.financingStatus;
  return (
    financingStatus === "approved" ||
    job.financials?.paymentPlan?.type === "financed"
  );
}

/** Compute outstanding $ owed: plan-pending - already-paid offset, floored at 0. */
function computeOutstandingAmount(job: Job): number {
  const plan = job.financials?.paymentPlan;
  if (!plan) {
    const contract = job.financials?.contractValue ?? 0;
    const paid = (job.financials?.payments ?? []).reduce((s, p) => s + p.amount, 0);
    return Math.max(0, contract - paid);
  }
  const pending = plan.lineItems
    .filter(li => li.status !== "paid" && li.status !== "waived")
    .reduce((s, li) => s + li.amount, 0);
  return Math.max(0, pending);
}

/**
 * The CCC next-morning rule. Returns null when:
 *  - job is not operationally completed
 *  - completion timing cannot be derived (fail conservative)
 *  - completion was today (silent grace period)
 *  - derived payment is fully settled
 *  - outstanding amount is sub-dollar noise
 */
export function detectPostCompletionFinancialException(
  job: Job,
  derivedPayment: UnifiedPaymentState,
): PostCompletionFinancialException | null {
  if (job.currentStage !== "Completed") return null;
  if (derivedPayment === "financially_cleared") return null;

  const completedAt = deriveCompletionDate(job);
  if (!completedAt) return null;

  const completedMs = new Date(completedAt).getTime();
  if (Number.isNaN(completedMs)) return null;
  if (completedMs >= startOfTodayMs()) return null; // completed today → silent

  const outstanding = computeOutstandingAmount(job);
  if (outstanding < 1) return null; // sub-dollar = noise, not exposure

  // Branch: financing approved + not yet settled → "funding not confirmed"
  // (inferred mapping; no dedicated disbursement field exists today).
  return {
    kind: isFinancedDeal(job) ? "funding_not_confirmed" : "cash_not_collected",
    outstandingAmount: outstanding,
    completedAt,
  };
}

// ── Follow-Up Integrity Check ──

export interface FollowUpIntegrityResult {
  valid: boolean;
  issues: string[];
}

export function checkFollowUpIntegrity(
  job: Job,
  wrapRecords: InstallerWrapRecord[],
  followUpTasks: { jobId: string; reason: string; status?: string }[],
): FollowUpIntegrityResult {
  const issues: string[] = [];
  const jobWraps = wrapRecords.filter(w => w.jobId === job.id);
  const jobFollowUps = followUpTasks.filter(f => f.jobId === job.id);

  // Every incomplete wrap should have a follow-up
  for (const wrap of jobWraps) {
    if (wrap.schedulingFollowUpRequired) {
      const hasFollowUp = jobFollowUps.some(f => f.jobId === job.id);
      if (!hasFollowUp) {
        issues.push(`Wrap ${wrap.id} requires follow-up but none exists`);
      }
    }
  }

  // Check for orphaned follow-ups (no matching wrap)
  for (const fu of jobFollowUps) {
    if (!fu.status || fu.status === "pending") {
      // Ensure there's a reason and it's actionable
      if (!fu.reason) {
        issues.push(`Follow-up task missing reason for job ${fu.jobId}`);
      }
    }
  }

  return { valid: issues.length === 0, issues };
}
